import { Controller } from "@hotwired/stimulus"

// Guards a date input against an accidental "weird" date - most often the wrong year picked from the
// native date picker. The input starts on today's date (the usual answer), a warning appears when the
// chosen date is more than `thresholdDays` away from today, and submitting such a date requires an
// explicit confirm. The submit button's own listener runs before any document-delegated handler, so
// cancelling here stops the submit from ever firing.
//
//   data-controller="date-sanity-check"
//   data-date-sanity-check-threshold-days-value="7"   (optional, defaults to 7)
//   input:   data-date-sanity-check-target="input" data-action="change->date-sanity-check#check"
//   warning: data-date-sanity-check-target="warning" (hidden with d-none until needed)
//   button:  data-action="click->date-sanity-check#confirm"
export default class extends Controller {
  static targets = ["input", "warning"]
  static values = { thresholdDays: { type: Number, default: 7 } }

  connect() {
    if (!this.inputTarget.value) this.inputTarget.value = this.todayString()
    this.check()
  }

  check() {
    const daysAway = this.daysFromToday()
    const unusual = daysAway !== null && Math.abs(daysAway) > this.thresholdDaysValue

    this.warningTarget.textContent = unusual ? this.describe(daysAway) : ""
    this.warningTarget.classList.toggle("d-none", !unusual)
  }

  confirm(event) {
    const daysAway = this.daysFromToday()
    if (daysAway === null || Math.abs(daysAway) <= this.thresholdDaysValue) return

    const message = `${this.describe(daysAway)}\n\nAre you sure ${this.inputTarget.value} is the right date?`
    if (!window.confirm(message)) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.inputTarget.focus()
    }
  }

  // Whole days between today and the input's date (negative = in the past), or null when empty.
  daysFromToday() {
    const value = this.inputTarget.value
    if (!value) return null

    const [year, month, day] = value.split("-").map(Number)
    const picked = Date.UTC(year, month - 1, day)
    const now = new Date()
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())

    return Math.round((picked - today) / 86400000)
  }

  describe(daysAway) {
    const count = Math.abs(daysAway)
    const direction = daysAway < 0 ? "ago" : "from now"
    return `Heads up: this date is ${count} day${count === 1 ? "" : "s"} ${direction}. Double-check the year.`
  }

  todayString() {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    return `${now.getFullYear()}-${month}-${day}`
  }
}
